import React from 'react';

import { useDraggable } from '@dnd-kit/core';

import Button from '@atlaskit/button/standard-button';
import { Checkbox } from '@atlaskit/checkbox';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu';
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Lozenge from '@atlaskit/lozenge';

import { IssuePriorityIcon } from '@qality/common-components/src/atoms/IssuePriorityIcon';
import { Table } from '@qality/common-components/src/atoms/Table';
import { User } from '@qality/common-components/src/atoms/User/User';
import { Issue } from '@qality/common-components/src/molecules/Issue';
import { FEATURE_FLAG } from '@qality/common-config';
import {
  Folder,
  Issue as IssueType,
  RepositoryIssue,
} from '@qality/common-model';
import { TestCaseRepositoryIssue } from '@qality/common-pages';
import { TestCaseRepositoryDndUtils } from '@qality/common-pages/src/TestCaseRepositoryPage/TestCaseRepositoryDnd/utils';
import { StorageOfUniqueServices } from '@qality/common-service';
import { EventUtils, StatusUtils } from '@qality/common-utils';

import { useTestCaseRepositoryFoldersContext } from '../../../TestCaseRepositoryFoldersProvider';
import { useTestCaseRepositoryModalsContext } from '../../../TestCaseRepositoryModalsProvider';
import { ColumnsWidths } from '../../TestCaseRepositoryTable';
import { DropdownItemDeleteFromFolderStyled } from '../../TestCaseRepositoryTable.styled';

export type Props = {
  issue: RepositoryIssue;
  isSelected: boolean;
  toggleSelectIssue: (issue: IssueType) => void;
  folder?: Folder;
  style?: React.CSSProperties;
};

export function TestCaseRepositoryTableRow({
  issue,
  isSelected,
  toggleSelectIssue,
  folder = null,
  style = {},
}: Props): JSX.Element {
  const { openAddToFolderModal, openDeleteFromFolderModal } =
    useTestCaseRepositoryModalsContext();
  const { currentView } = useTestCaseRepositoryFoldersContext();
  const issueUrl = StorageOfUniqueServices.get('URLService').getIssueURL(
    issue.key
  );

  const projectUrl = StorageOfUniqueServices.get('URLService').getProjectUrl(
    issue.fields.project.key
  );

  const statusAppearance = StatusUtils.getAppearanceType(
    issue.fields.status.statusCategory.colorName
  );

  const isDeleteFromFolderVisible =
    currentView === 'folder' && issue.testCaseInFolder?.id && folder?.canEdit;

  const isDraggable = FEATURE_FLAG.isPremium;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    active,
    isDragging,
  } = useDraggable({
    id: TestCaseRepositoryDndUtils.composeId({
      item: 'issue',
      id: Number(issue.testCaseInFolder?.id ?? issue.id),
    }),
    data: TestCaseRepositoryDndUtils.composeData({
      type: 'issue',
      payload: { issue },
    }),
    disabled: !isDraggable,
  });

  return (
    <Table.DndTr
      isHoverable
      isMuted={isDragging || (active && isSelected)}
      innerRef={setNodeRef}
      style={style}
      hideOverflow
    >
      {isDraggable ? (
        <Table.DndTd
          widths={ColumnsWidths.dnd}
          innerRef={setActivatorNodeRef}
          {...listeners}
          {...attributes}
        >
          <Table.ContentHelper alignItems='center'>
            <DragHandlerIcon label='Drag and drop handler icon' />
          </Table.ContentHelper>
        </Table.DndTd>
      ) : (
        <Table.Td>{/* Empty */}</Table.Td>
      )}

      <Table.CheckboxTd widths={ColumnsWidths.checkbox}>
        <Table.ContentHelper
          justifyContent='center'
          alignItems='center'
          onClick={EventUtils.stopPropagation}
        >
          <Checkbox
            onChange={() => toggleSelectIssue(issue)}
            isChecked={isSelected}
          />
        </Table.ContentHelper>
      </Table.CheckboxTd>

      <Table.Td widths={ColumnsWidths.key}>
        <Table.ContentHelper overflow='hidden'>
          <TestCaseRepositoryIssue issue={issue} issueLink={issueUrl} />
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td widths={ColumnsWidths.summary}>
        <Table.ContentHelper overflow='hidden'>
          <Issue
            addPadding
            withEllipsis
            title={issue.fields.summary}
            issueLink={issueUrl}
          />
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td widths={ColumnsWidths.project}>
        <Table.ContentHelper overflow='hidden'>
          <Issue
            withEllipsis
            title={issue.fields.project.name}
            iconSrc={issue.fields.project.avatarUrls['16x16']}
            issueLink={projectUrl}
          />
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td widths={ColumnsWidths.priority}>
        <Table.ContentHelper justifyContent='center' alignItems='center'>
          <IssuePriorityIcon priority={issue.fields.priority} />
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td widths={ColumnsWidths.status}>
        <Table.ContentHelper alignItems='center'>
          <Lozenge appearance={statusAppearance}>
            {issue.fields.status.name}
          </Lozenge>
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td widths={ColumnsWidths.assignee}>
        <Table.ContentHelper justifyContent='center' alignItems='center'>
          <User
            userName={issue.fields.assignee?.displayName ?? 'Unassigned'}
            userAvatar={issue.fields.assignee?.avatarUrls['24x24']}
            size='small'
            isTextVisible={false}
          />
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td widths={ColumnsWidths.menu} className='show-on-row-hover'>
        <Table.ContentHelper justifyContent='center' alignItems='center'>
          <DropdownMenu
            shouldFlip={false}
            placement='bottom-end'
            trigger={({ triggerRef, ...props }) => (
              <Button
                {...props}
                iconBefore={
                  <MoreIcon
                    size='medium'
                    primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                    label='moreIcon'
                  />
                }
                ref={triggerRef}
                appearance='subtle'
              />
            )}
          >
            <DropdownItem
              key='add-to-folder'
              onClick={() =>
                openAddToFolderModal({
                  testCasesIds: [issue.id as number],
                })
              }
            >
              Add to folder
            </DropdownItem>
            {isDeleteFromFolderVisible && (
              <DropdownItem
                key='add-to-folder'
                onClick={() =>
                  openDeleteFromFolderModal({
                    testCasesInFolder: [issue.testCaseInFolder],
                    folder,
                  })
                }
              >
                <DropdownItemDeleteFromFolderStyled>
                  Remove from folder
                </DropdownItemDeleteFromFolderStyled>
              </DropdownItem>
            )}
          </DropdownMenu>
        </Table.ContentHelper>
      </Table.Td>
    </Table.DndTr>
  );
}
